/**
 * ResumeDraftsCard — surfaces any in-flight autosave drafts.
 * Iter 70.
 *
 * Scans localStorage for any draft saved by `useAutosave` ≥ 1 h ago but
 * ≤ 7 d ago, and lists them as one-click reopen cards. Aims to make the
 * (otherwise invisible) autosave feature visible + valuable.
 *
 * Draft keys have the shape:
 *   `physiogenic.autosave.v1.<scope>:<pid>[:<extra>]`
 * Where scope ∈ {interview, objective, treatment-plan, clinical-note}.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ClipboardList, Activity, Stethoscope, X, ChevronRight, Clock } from 'lucide-react';

const KEY_PREFIX = 'physiogenic.autosave.v1.';

const SCOPE_META = {
  'interview': {
    label: 'Patient interview',
    icon: ClipboardList,
    color: '#003D80',
    routeStaff: (pid) => `/admin/patients/${pid}/interview`,
    routePatient: () => '/patient/interview',
  },
  'objective': {
    label: 'Objective examination',
    icon: Stethoscope,
    color: '#7B4FA5',
    routeStaff: (pid) => `/admin/patients/${pid}/objective-assessment`,
    routePatient: null,
  },
  'treatment-plan': {
    label: 'Treatment plan',
    icon: Activity,
    color: '#10A862',
    routeStaff: (pid) => `/admin/patients/${pid}/treatment-plan`,
    routePatient: () => '/patient/treatment-plan',
  },
  'clinical-note': {
    label: 'Clinical note',
    icon: FileText,
    color: '#D86E2C',
    routeStaff: (pid, extra) => `/admin/patients/${pid}/note${extra && extra !== 'free' ? `?appointment_id=${extra}` : ''}`,
    routePatient: null,
  },
};

function parseDrafts() {
  if (typeof window === 'undefined') return [];
  const out = [];
  const now = Date.now();
  const MIN_AGE = 60 * 60 * 1000;       // 1 h
  const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 d
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(KEY_PREFIX)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      let parsed;
      try { parsed = JSON.parse(raw); } catch { continue; }
      const at = parsed?.at;
      if (!at || (now - at) < MIN_AGE || (now - at) > MAX_AGE) continue;
      const tail = k.slice(KEY_PREFIX.length);
      // tail = "scope:pid" or "scope:pid:extra"
      const [scope, pid, extra] = tail.split(':');
      const meta = SCOPE_META[scope];
      if (!meta) continue;
      out.push({ key: k, at, scope, pid, extra, meta, value: parsed.value });
    }
  } catch { /* ignore */ }
  return out.sort((a, b) => b.at - a.at);
}

function relativeTime(at) {
  const sec = Math.max(1, Math.floor((Date.now() - at) / 1000));
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return `${Math.floor(sec / 86400)} d ago`;
}

/**
 * Pulls one "preview" snippet out of a draft so the user can recognise it.
 */
function previewOf(draft) {
  const v = draft.value || {};
  if (draft.scope === 'interview') {
    return v.chief_complaint || v.symptom_description || v.history_of_present_illness || '';
  }
  if (draft.scope === 'objective') {
    return v.diagnosis || v.therapist_notes || (v.vitals?.bp ? `BP ${v.vitals.bp}` : '');
  }
  if (draft.scope === 'treatment-plan') {
    return v.diagnosis || v.short_term_goals || v.long_term_goals || '';
  }
  if (draft.scope === 'clinical-note') {
    return v.subjective || v.data || v.focus || v.assessment || '';
  }
  return '';
}

export default function ResumeDraftsCard({ role = 'admin' }) {
  const [drafts, setDrafts] = useState(() => parseDrafts());
  const [hidden, setHidden] = useState(false);

  // Re-scan on focus so clearing a draft in one tab updates here.
  useEffect(() => {
    const refresh = () => setDrafts(parseDrafts());
    window.addEventListener('focus', refresh);
    const id = setInterval(refresh, 60_000);
    return () => { window.removeEventListener('focus', refresh); clearInterval(id); };
  }, []);

  const dismiss = (key) => {
    try { localStorage.removeItem(key); } catch {}
    setDrafts(parseDrafts());
  };

  const dismissAll = () => {
    drafts.forEach(d => { try { localStorage.removeItem(d.key); } catch {} });
    setDrafts([]);
  };

  if (hidden || drafts.length === 0) return null;

  return (
    <div className="bento-card mb-6 border-l-4 border-l-[#FFB000]" data-testid="resume-drafts-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#FFB000]/15 flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#FFB000]"/>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-sm">Resume where you left off</h3>
            <p className="text-[11px] text-fg-muted">
              {drafts.length} unsaved draft{drafts.length === 1 ? '' : 's'} waiting — click to continue editing.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={dismissAll}
                  className="text-[10px] uppercase tracking-wider text-fg-muted hover:text-[#B83A3A]"
                  data-testid="resume-drafts-dismiss-all">
            Dismiss all
          </button>
          <button onClick={() => setHidden(true)}
                  className="p-1 text-fg-muted hover:text-fg"
                  data-testid="resume-drafts-hide">
            <X className="w-3.5 h-3.5"/>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="resume-drafts-grid">
        {drafts.slice(0, 6).map(d => {
          const Icon = d.meta.icon;
          const isStaff = role === 'admin' || role === 'clinician';
          const route = isStaff
            ? d.meta.routeStaff?.(d.pid, d.extra)
            : d.meta.routePatient?.();
          if (!route) return null;
          const preview = (previewOf(d) || '').slice(0, 90);
          return (
            <Link to={route}
                  key={d.key}
                  className="flex items-start gap-3 p-3 rounded-md border border-soft hover:border-brand hover:bg-brand/5 transition group"
                  data-testid={`resume-draft-${d.scope}-${d.pid}`}>
              <div className="w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center text-white" style={{ background: d.meta.color }}>
                <Icon className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-fg">{d.meta.label}</span>
                  <span className="text-[10px] text-fg-muted">· {relativeTime(d.at)}</span>
                </div>
                {preview && (
                  <div className="text-[11px] text-fg-muted line-clamp-2 mt-0.5 italic">"{preview}…"</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-fg-muted group-hover:text-brand transition"/>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(d.key); }}
                        className="text-[9px] text-fg-muted hover:text-[#B83A3A] opacity-0 group-hover:opacity-100 transition"
                        title="Discard this draft"
                        data-testid={`resume-draft-discard-${d.scope}-${d.pid}`}>
                  discard
                </button>
              </div>
            </Link>
          );
        })}
      </div>
      {drafts.length > 6 && (
        <div className="text-[10px] text-fg-muted text-center mt-2">+{drafts.length - 6} more drafts older than the most recent six.</div>
      )}
    </div>
  );
}
